import { computed, Injectable, signal } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Injectable({
  providedIn: 'root',
})
export class MarkdownRendererService {
  // Signal for caching rendered content
  private readonly renderCache = signal<Map<string, SafeHtml>>(new Map());

  constructor(private readonly sanitizer: DomSanitizer) {}

  private createActionListRegex(global = false): RegExp {
    const flags = global ? 'g' : '';
    return new RegExp(
      '\\{\\s*"type"\\s*:\\s*"action_list"\\s*,\\s*"itemList"\\s*:\\s*\\[(?:[\\s\\S]*?)\\]\\s*\\}',
      flags
    );
  }

  // Signal-based rendering with caching
  renderToHtmlSignal(text: string) {
    return computed(() => {
      const cache = this.renderCache();
      const cached = cache.get(text);
      if (cached) {
        return cached;
      }

      const rendered = this.renderToHtml(text);
      this.renderCache.update(cache => {
        const newCache = new Map(cache);
        newCache.set(text, rendered);
        return newCache;
      });

      return rendered;
    });
  }

  renderToHtml(text: string): SafeHtml {
    if (!text?.trim()) {
      return this.sanitizer.bypassSecurityTrustHtml('');
    }

    const processedText = this.processCustomComponents(text);
    const html = this.markdownToHtml(processedText);
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  private processCustomComponents(text: string): string {
    let processed = text;

    // Strip assistant control tags that shouldn't be displayed
    processed = processed.replace(/<\/?(tool|response)>/gi, '');

    // Handle <thinking> blocks
    processed = processed.replace(
      /<thinking>(.*?)<\/thinking>/gs,
      (match, content) => {
        const cleanContent = content.trim();
        return `<div class="custom-component" data-type="thinking" data-content="${this.escapeHtml(cleanContent)}"></div>`;
      }
    );

    // Handle JSON action lists
    processed = processed.replace(
      /```json\s*(\{[\s\S]*?\})\s*```/gi,
      (match, jsonPayload) => {
        try {
          const data = JSON.parse(jsonPayload);
          if (data?.type === 'action_list' && Array.isArray(data?.itemList)) {
            const escapedData = this.escapeHtml(JSON.stringify(data));
            return `<div class="custom-component" data-type="action_list" data-content="${escapedData}"></div>`;
          }
        } catch (error) {
          console.warn('Failed to parse action list code block JSON:', error);
        }
        return match;
      }
    );

    processed = processed.replace(this.createActionListRegex(true), match => {
      try {
        const data = JSON.parse(match);
        const escapedData = this.escapeHtml(JSON.stringify(data));
        return `<div class="custom-component" data-type="action_list" data-content="${escapedData}"></div>`;
      } catch (error) {
        console.warn('Failed to parse action list JSON:', error);
        return match; // Return original if parsing fails
      }
    });

    // Replace <action-card>...</action-card> with placeholder (raw JSON hidden)
    processed = processed.replace(
      /<action-card>[\s\S]*?<\/action-card>/gi,
      '<div class="custom-component" data-type="action_card"></div>'
    );

    // Remove stray closing thinking tags if any remain
    processed = processed.replace(/<\/thinking>/gi, '');

    return processed;
  }

  /**
   * Checks if text contains custom components that need special rendering
   */
  hasCustomComponents(text: string): boolean {
    return (
      /<thinking>.*?<\/thinking>/.test(text) ||
      this.createActionListRegex().test(text) ||
      /<action-card>/i.test(text)
    );
  }

  /**
   * Signal-based method to check for custom components
   */
  hasCustomComponentsSignal(text: string) {
    return computed(() => this.hasCustomComponents(text));
  }

  /**
   * Extracts custom component data from text
   */
  extractCustomComponents(
    text: string
  ): Array<{ type: string; data: unknown; originalMatch: string }> {
    const components: Array<{
      type: string;
      data: unknown;
      originalMatch: string;
    }> = [];
    const seenMatches = new Set<string>();

    const pushComponent = (
      originalMatch: string,
      data: unknown,
      dedupeKey: string
    ) => {
      if (seenMatches.has(dedupeKey)) return;
      seenMatches.add(dedupeKey);
      components.push({
        type: 'action_list',
        data,
        originalMatch,
      });
    };

    // Extract thinking blocks
    const thinkingMatches = text.matchAll(/<thinking>(.*?)<\/thinking>/gs);
    for (const match of thinkingMatches) {
      components.push({
        type: 'thinking',
        data: { content: match[1].trim() },
        originalMatch: match[0],
      });
    }

    // Extract action lists from code fences
    const fencedMatches = text.matchAll(/```json\s*(\{[\s\S]*?\})\s*```/gi);
    for (const match of fencedMatches) {
      try {
        const jsonContent = match[1];
        const data = JSON.parse(jsonContent);
        pushComponent(match[0], data, jsonContent);
      } catch (error) {
        console.warn(
          'Failed to parse action list JSON from code fence:',
          error
        );
      }
    }

    // Extract action lists
    const actionListMatches = text.matchAll(this.createActionListRegex(true));
    for (const match of actionListMatches) {
      try {
        const data = JSON.parse(match[0]);
        pushComponent(match[0], data, match[0]);
      } catch (error) {
        console.warn('Failed to parse action list JSON:', error);
      }
    }

    // Extract <action-card>...</action-card> (e.g. send-email)
    const actionCardRegex = /<action-card>([\s\S]*?)<\/action-card>/gi;
    let actionCardMatch;
    while ((actionCardMatch = actionCardRegex.exec(text)) !== null) {
      try {
        const jsonStr = actionCardMatch[1].trim();
        const data = JSON.parse(jsonStr);
        if (data && typeof data.type === 'string' && data.card) {
          components.push({
            type: 'action_card',
            data,
            originalMatch: actionCardMatch[0],
          });
        }
      } catch (error) {
        console.warn('Failed to parse action-card JSON:', error);
      }
    }

    return components;
  }

  /**
   * Signal-based method to extract custom components
   */
  extractCustomComponentsSignal(text: string) {
    return computed(() => this.extractCustomComponents(text));
  }

  private markdownToHtml(text: string): string {
    const normalized = text.replace(/\r\n/g, '\n');
    const lines = normalized.split('\n');
    const htmlParts: string[] = [];
    let inUl = false;
    let inOl = false;
    let inTable = false;
    let paragraphBuffer: string[] = [];

    const closeUl = (): void => {
      if (inUl) {
        htmlParts.push('</ul>');
        inUl = false;
      }
    };

    const closeOl = (): void => {
      if (inOl) {
        htmlParts.push('</ol>');
        inOl = false;
      }
    };

    const closeLists = (): void => {
      closeUl();
      closeOl();
    };

    const flushParagraph = (): void => {
      if (!paragraphBuffer.length) return;
      htmlParts.push(`<p>${paragraphBuffer.join('<br/>')}</p>`);
      paragraphBuffer = [];
    };

    const isTableRow = (line: string): boolean => {
      return line.trim().includes('|') && line.trim().split('|').length > 2;
    };

    const isTableSeparator = (line: string): boolean => {
      const trimmed = line.trim();
      return /^\|?[\s\-:|]+\|[\s\-:|]+\|?$/.test(trimmed);
    };

    const parseTableRow = (line: string, isHeader: boolean): string => {
      const cells = line
        .trim()
        .split('|')
        .map(cell => cell.trim())
        .filter((cell, index, array) => {
          // Remove empty cells at start/end (from leading/trailing pipes)
          return cell !== '' || (index !== 0 && index !== array.length - 1);
        });

      const tag = isHeader ? 'th' : 'td';
      const cellsHtml = cells
        .map(cell => `<${tag}>${this.parseInline(cell)}</${tag}>`)
        .join('');

      return `<tr>${cellsHtml}</tr>`;
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i] ?? '';

      // Check for table start
      if (
        !inTable &&
        isTableRow(line) &&
        i + 1 < lines.length &&
        isTableSeparator(lines[i + 1])
      ) {
        flushParagraph();
        closeLists();
        inTable = true;

        // Start table with wrapper and header
        htmlParts.push('<div class="table-wrapper">');
        htmlParts.push('<table>');
        htmlParts.push('<thead>');
        htmlParts.push(parseTableRow(line, true));
        htmlParts.push('</thead>');
        htmlParts.push('<tbody>');

        // Skip the separator line
        i++;
        continue;
      }

      // Continue table with body rows
      if (inTable && isTableRow(line)) {
        htmlParts.push(parseTableRow(line, false));
        continue;
      }

      // End table if we're in one and hit a non-table line
      if (inTable) {
        htmlParts.push('</tbody>');
        htmlParts.push('</table>');
        htmlParts.push('</div>');
        inTable = false;

        // Process the current line normally if it's not empty
        if (line.trim().length === 0) {
          continue;
        }
      }

      // Unordered lists
      const unorderedMatch = line.match(/^\s*[-*]\s+(.+)/);
      if (unorderedMatch) {
        flushParagraph();
        closeOl();
        if (!inUl) {
          htmlParts.push('<ul>');
          inUl = true;
        }
        htmlParts.push(`<li>${this.parseInline(unorderedMatch[1])}</li>`);
        continue;
      }

      // Ordered lists
      const orderedMatch = line.match(/^\s*(\d+)\.\s+(.+)/);
      if (orderedMatch) {
        flushParagraph();
        closeUl();
        if (!inOl) {
          htmlParts.push('<ol>');
          inOl = true;
        }
        htmlParts.push(`<li>${this.parseInline(orderedMatch[2])}</li>`);
        continue;
      }

      // Headings
      const headingMatch = line.match(/^\s*(#{1,3})\s+(.+)/);
      if (headingMatch) {
        const level = Math.min(headingMatch[1].length, 3);
        flushParagraph();
        closeLists();
        htmlParts.push(
          `<h${level}>${this.parseInline(headingMatch[2])}</h${level}>`
        );
        continue;
      }

      // Empty lines
      if (line.trim().length === 0) {
        flushParagraph();
        closeLists();
        continue;
      }

      // Regular paragraphs
      closeLists();
      paragraphBuffer.push(this.parseInline(line));
    }

    flushParagraph();
    closeLists();

    // Close table if still open at end
    if (inTable) {
      htmlParts.push('</tbody>');
      htmlParts.push('</table>');
      htmlParts.push('</div>');
    }

    return htmlParts.join('');
  }

  private parseInline(value: string): string {
    let escaped = this.escapeHtml(value);

    // Handle code blocks first to avoid conflicts
    const codePlaceholders: string[] = [];
    escaped = escaped.replace(/`([^`]+)`/g, (_match, code) => {
      const placeholder = `@@CODE${codePlaceholders.length}@@`;
      codePlaceholders.push(`<code>${this.escapeHtml(code)}</code>`);
      return placeholder;
    });

    // Handle links
    const linkPlaceholders: string[] = [];
    escaped = escaped.replace(
      /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
      (_match, label, url) => {
        const placeholder = `@@LINK${linkPlaceholders.length}@@`;
        linkPlaceholders.push(
          `<a href="${this.escapeHtml(url)}" target="_blank" rel="noopener noreferrer">${this.escapeHtml(label)}</a>`
        );
        return placeholder;
      }
    );

    // Bold text
    escaped = escaped.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    escaped = escaped.replace(/__([^_]+)__/g, '<strong>$1</strong>');

    // Italic text
    escaped = escaped.replace(/\*(?!\s)([^*]+?)\*(?!\s)/g, '<em>$1</em>');
    escaped = escaped.replace(/_(?!\s)([^_]+?)_(?!\s)/g, '<em>$1</em>');

    // Restore placeholders
    linkPlaceholders.forEach((html, index) => {
      escaped = escaped.replace(`@@LINK${index}@@`, html);
    });

    codePlaceholders.forEach((html, index) => {
      escaped = escaped.replace(`@@CODE${index}@@`, html);
    });

    return escaped;
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}
